import api from './baseApi';
import type {
    LibraryCategory,
    Library,
    LibraryResponse,
    LibraryType,
    LibraryElement,
    CreateLibraryDTO,
    DisplayOptionMode,
    OptionType,
    LibraryUsageResult,
    LibraryDetail,
    LibraryDetailElement,
    CategoryElementsUpdate
} from '@/typings/libraries';

const controller = 'Libraries';

export const getLibraryTypes = (): Promise<LibraryType[]> =>
    api
        .get<LibraryType[]>(`/${controller}/Types`, { cache: { maxAge: 60 * 60 * 1000 } })
        .then(({ data }) => data);

export const createLibrary = (name: string, colorMode: string | number, type: number, thumbnailPath?: string) =>
    api
        .post(`/${controller}`, null, {
            params: { name, colorMode, type, thumbnailPath }
        })
        .then(({ data }) => data);

export const editLibrary = (
    libraryId: number,
    name: string,
    thumbnailPath?: string,
    defaultOptionType?: OptionType,
    displayOptionAs?: DisplayOptionMode
) =>
    api.put<void>(`/${controller}/${libraryId}`, null, {
        params: { name, thumbnailPath, defaultOptionType, displayOptionAs }
    });

export const getLibrary = (id: number): Promise<Library> => api.get(`/${controller}/${id}`).then(({ data }) => data);

export const getLibraries = (
    typeIds: number[],
    pageNumber: number,
    pageSize: number,
    searchString = '',
    deleted = false,
    sortBy?: string,
    sortMode?: string
): Promise<LibraryResponse> =>
    api
        .get<LibraryResponse>(`/${controller}/GetLibrariesByTypes`, {
            params: { searchString, pageNumber, pageSize, typeIds, deleted, sortBy, sortMode }
        })
        .then(({ data }) => data);

export const deleteLibrary = (id: number) => api.delete(`/${controller}/${id}`);

export const restoreLibrary = (id: number) => api.put(`/${controller}/${id}/Restore`);

export const cloneLibrary = (libraryId: number) =>
    api.post(`/${controller}`, null, { params: { sourceId: libraryId } });

export const createLibraryCategory = (
    libraryId: number,
    category: Partial<Pick<LibraryCategory, 'Name' | 'ThumbnailPath' | 'Path' | 'OptionThumbnailPath'>>
) => {
    return api
        .post(`/${controller}/${libraryId}/Categories`, null, {
            params: {
                category: JSON.stringify(category)
            }
        })
        .then(({ data }) => data);
};

export const getLibraryCategoriesCount = (libraryId: number): Promise<number> =>
    api.get<number>(`/${controller}/${libraryId}/Categories/Count`).then(({ data }) => data);

export const getLibraryCategories = (
    libraryId: number,
    from: number,
    count: number,
    searchString = '',
    sortBy?: string,
    sortMode?: 'ASC' | 'DESC'
): Promise<LibraryCategory[]> =>
    api
        .get(`/${controller}/${libraryId}/Categories`, {
            params: {
                searchString,
                from,
                count,
                sortBy,
                sortMode
            }
        })
        .then(({ data }) => data);

export const getLibraryCategoryById = (libraryId: number, categoryId: number): Promise<LibraryCategory> =>
    api.get(`/${controller}/${libraryId}/Categories/${categoryId}`).then(({ data }) => data);

export const deleteCategory = (libraryId: number, categoryId: number) =>
    api.delete(`/${controller}/${libraryId}/Categories/${categoryId}`);

export const updateLibraryCategory = (libraryId: number, categoryId: number, category: LibraryCategory) =>
    api.put<void>(`/${controller}/${libraryId}/Categories/${categoryId}`, null, {
        params: {
            category: JSON.stringify(category)
        }
    });

export const setDefaultSelectedItem = (libraryId: number, categoryId?: number, elementId?: number) =>
    api.put<void>(`/${controller}/${libraryId}/ChangeSelected`, null, {
        params: { categoryId, elementId }
    });

export const createLibraryElements = (libraryId: number, elements: CreateLibraryDTO) =>
    api.post(`/${controller}/${libraryId}/AddElements`, elements);

export const updateLibraryElement = (
    libraryId: number,
    elementId: number,
    element: LibraryElement,
    libraryType: number
) =>
    api.put<void>(`/${controller}/${libraryId}/${elementId}`, null, {
        params: {
            element: JSON.stringify(element),
            type: libraryType
        }
    });

export const updateElementsCategory = (libraryId: number, payload: CategoryElementsUpdate) =>
    api.put<void>(`/${controller}/${libraryId}/ChangeElementsCategory`, payload);

export const getLibraryElements = (
    libraryId: number,
    from: number,
    num: number,
    searchString = '',
    categoryId?: number,
    sortBy?: string,
    sortMode?: 'ASC' | 'DESC'
): Promise<LibraryElement[]> => {
    const url = categoryId
        ? `/${controller}/${libraryId}/Categories/${categoryId}/Elements/Private`
        : `/${controller}/${libraryId}/Elements`;
    return api.get(url, { params: { searchString, from, num, count: num, sortBy, sortMode } }).then(({ data }) => data);
};

export const getLibraryElementsCount = (libraryId: number, categoryId?: number): Promise<number> => {
    const url = categoryId
        ? `/${controller}/${libraryId}/Categories/${categoryId}/Elements/Count`
        : `/${controller}/${libraryId}/Elements/Count`;
    return api.get<number>(url).then(({ data }) => data);
};

export const deleteLibraryElement = (elementId: number, position: number) =>
    api.delete(`/${controller}/${elementId}/${position}`);

export const deleteMultipleLibraryElement = (libraryId: number, elementIds: number[]) =>
    api.delete(`/${controller}/${libraryId}/BulkDelete`, { params: { elementIds } });

export const getLibraryElementsByCategory = (
    libraryId: number,
    categoryId: number,
    from: number,
    count: number,
    searchString = ''
) => {
    return api.get(`/${controller}/${libraryId}/Categories/${categoryId}/Elements/Private`, {
        params: { searchString, from, count }
    });
};

export const getElementsByCategoryCount = (libraryId: number, categoryId: number) =>
    api.get(`/${controller}/${libraryId}/Categories/${categoryId}/Elements/Count`);

export const updateLibrarySortingMode = (
    libraryId: number,
    sortType: number,
    libraryType: number,
    categoryId?: string | number,
    isCategory = false
): Promise<Required<AsyncJob>> => {
    let url = `/${controller}/${libraryId}`;
    url += isCategory ? `/Categories/Sort` : `/Sort`;

    return api.put(url, null, { params: { categoryId, sortType, type: libraryType } }).then(({ data }) => data);
};

export const syncLibrarySorting = (
    libraryId: number,
    categoryId?: string | number,
    isCategory = false
): Promise<Required<AsyncJob>> => {
    const url = isCategory
        ? `/${controller}/${libraryId}/Settings/Sync/Categories`
        : `/${controller}/${libraryId}/Settings/Sync`;

    return api.post(url, null, { params: { categoryId } }).then(({ data }) => data);
};

export interface AsyncJob {
    id?: number;
    progressIdentifier: string;
}

export interface TaskProgressResponse {
    error: boolean;
    errorMessage: string;
    progress: number;
}

export const bulkUploadLibrary = (fileName: string, identifier: string): Promise<AsyncJob> =>
    api
        .post<AsyncJob>(`/${controller}/BulkUpload`, {
            ZipName: fileName,
            ZipIdentifier: identifier
        })
        .then(({ data }) => data);

export const getTaskProgress = (progressIdentifier: string): Promise<TaskProgressResponse> =>
    api.get<TaskProgressResponse>(`/TasksProgress/${progressIdentifier}`).then(({ data }) => data);

export const getSyncLibraryTaskProgress = (taskId: string, libraryId: number): Promise<TaskProgressResponse> => {
    return api
        .get<TaskProgressResponse>(`/Libraries/${libraryId}/TaskProgress?taskProgressId=${taskId}`)
        .then(({ data }) => data);
};

export const getLibraryUsages = (
    libraryId: number,
    libraryType: number,
    usageType: number,
    searchString: string,
    from: number,
    count: number
): Promise<LibraryUsageResult> => {
    return api
        .get<LibraryUsageResult>(`/${controller}/${libraryId}/UsageInfo`, {
            params: { libraryType, usageType, searchString, from, count }
        })
        .then(({ data }) => data);
};

export const getLibraryDetails = (libraryId: number): Promise<LibraryDetail> =>
    api.get(`/${controller}/${libraryId}/Details`).then(({ data }) => data);

export const getLibraryElementDetails = (
    libraryId: number,
    libraryType: number,
    categoryId?: number,
    searchString?: string,
    elementPosition?: number
): Promise<LibraryDetailElement[]> =>
    api
        .get(`/${controller}/${libraryId}/Elements`, {
            params: { libraryType, categoryId, searchString, elementPosition }
        })
        .then(({ data }) => data);
